/**
 * Application Constants
 * Shared constants used across the application
 */

/**
 * Global Application / Branding Constants
 * SkinType controls layout and UI variants throughout AdminView and related components.
 *
 * SkinType values:
 *   '1' — Generic / default vertical panel button layout
 *   '2' — ChildNav: two-column answer layout (keyValue number + aText label)
 *   '3' — Patient Tools: fallback for any unrecognized/unmapped SkinType value
 *
 * AnswerLayout values (derived from SkinType, but can be overridden per-use):
 *   'verticalpanelbutton' — single centered-text button per answer
 *   'vertical2column'     — left column: numeric keyValue label; right column: aText
 *
 * Milestone parameterization:
 *   MILESTONE_YES_VALUE        — dataValue stored when child can do milestone
 *   MILESTONE_SOMETIMES_VALUE  — dataValue stored when child sometimes does milestone
 *   MILESTONE_NOTYET_VALUE     — dataValue stored when child cannot yet do milestone
 */
export const APP_CONSTANTS = {
  /** Default skin type for this product (ChildNav = '2') */
  DEFAULT_SKIN_TYPE: '2',

  /** Answer layout for each skin type */
  ANSWER_LAYOUT_BY_SKIN: {
    '1': 'verticalpanelbutton',
    '2': 'vertical2column',
    // TODO: real Patient Tools values — placeholder until the dedicated skin-3 visual pass
    '3': 'vertical2column',
  } as Record<string, string>,

  /** Fallback answer layout when skin type is not mapped */
  DEFAULT_ANSWER_LAYOUT: 'vertical2column',

  /**
   * Answer button / key-cell background color per skin type.
   * SkinType '2' (ChildNav) uses the app navigation green.
   */
  ANSWER_BUTTON_COLOR_BY_SKIN: {
    '1': '#3498db',   // Blue (generic)
    '2': '#10b981',   // ChildNav navigation green
    // TODO: real Patient Tools values — placeholder until the dedicated skin-3 visual pass
    '3': '#10b981',
  } as Record<string, string>,

  /** Fallback answer button color */
  DEFAULT_ANSWER_BUTTON_COLOR: '#10b981',

  /**
   * Common max-width (px) for the vertical2column answer row so it never
   * stretches to the full container width on wide screens.
   */
  ANSWER_ROW_MAX_WIDTH: 520,

  // ── Milestone answer dataValues ──────────────────────────────────────────
  MILESTONE_YES_VALUE:       '1',
  MILESTONE_SOMETIMES_VALUE: '2',
  MILESTONE_NOTYET_VALUE:    '3',
} as const;

export const API_CONSTANTS = {
  /**
   * Secure signature for account service requests
   */
  ACCOUNT_SERVICE_SIGNATURE: 'yBwo314C-O9lyrDq91RrbwcAQl23wpdBHcU0xiNCgCg',
  
  /**
   * Default access and skin types for account operations
   */
  DEFAULT_ACCESS_TYPE: '10',
  DEFAULT_SKIN_TYPE: '2',
  DEFAULT_CLIENT_ID: '7',
} as const;

/**
 * Authentication Constants
 */
export const AUTH_CONSTANTS = {
  /**
   * Legacy signature for authentication requests
   */
  LEGACY_AUTH_SIGNATURE: 'testsignature123',

  /**
   * Default authentication parameters
   */
  DEFAULT_AUTH_ACCESS_TYPE: '10',
  DEFAULT_AUTH_SKIN_TYPE: '2',
  DEFAULT_AUTH_CLIENT_ID: '7',
} as const;

/**
 * Canonical set of storage keys written by session establishment (`userStore.login`/
 * `applyAuthSession`) and cleared by logout. This is the single source of truth for
 * "what counts as session state" — any code that persists or clears a session should
 * iterate `ALL_AUTH_STORAGE_KEY_VALUES` rather than hand-rolling its own key list, so
 * logout/clear paths can't silently drift out of sync with what login/persist writes.
 *
 * Deliberately excludes web-only extras (`user_password`, `user_signature`, `user_name`,
 * `passkey_credential_id`) — those stay scoped to `WebAuthService.ts`'s own key map.
 */
export const AUTH_STORAGE_KEYS = {
  TOKEN: 'auth_token',
  REFRESH_TOKEN: 'refresh_token',
  TIMESTAMP: 'auth_timestamp',
  USER_SESSION: 'userSession',
  USER_EMAIL: 'user_email',
  ACCESS_TYPE: 'auth_access_type',
  NAV_RESET_ON_SIGNIN: 'progroups_navigation_reset_on_signin',
  USER_SETTINGS: 'user_settings',
} as const;

export const ALL_AUTH_STORAGE_KEY_VALUES: readonly string[] = Object.values(AUTH_STORAGE_KEYS);